import React from 'react';
import Fuse from 'fuse.js';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { Empty, Layout, Tabs, TabsProps } from 'antd';
import type { UnionExpand } from '@owl-lib/type-util';
import type { Citation } from '../../../api/claims/interface';
import type { ClaimFile } from '../../../api/files/interface';
import { Locale } from '../../../context/AppProvider';
import useRunHook from '../../../data/runs/useRunHook';
import { EscRouteDefs, generatePath } from '../../Routing/Routing';
import { ClaimDetailsContext, Params } from '../ClaimDetailsContext';
import useFilesHook from '../FilesListContainer/useFilesHook';
import styles from './ClaimFileDetailsContainer.module.scss';
import ClaimFileViewerContainer from './ClaimFileViewerContainer';

interface Props {
  file: ClaimFile;
  citationPage?: number;
  setFilePathUrl?: boolean;
  inDrawer?: boolean;
  curTocCitation?: Citation | null;
}

const getStatusDescriptionLocalePath = (
  ingestStatus: ClaimFile['ingest_status']
): string => {
  if (ingestStatus === 'done') {
    return 'claim_overview.files.documents_details.done';
  }

  if (ingestStatus === 'new') {
    return 'claim_overview.files.documents_details.new';
  }

  return 'claim_overview.files.documents_details.error';
};

const isFileViewable = (ingest_status: string): boolean => {
  return ingest_status === 'done' || ingest_status === 'preprocessed';
};

const ClaimFileDetailsContainer: React.FC<Props> = ({
  file,
  citationPage,
  setFilePathUrl,
  inDrawer = false,
  curTocCitation,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { dossierId } = useParams() as UnionExpand<Params>;
  const { messages } = React.useContext(Locale);
  const { claimMetadata } = React.useContext(ClaimDetailsContext);
  const [{ filesOCR }, filesHandler] = useFilesHook(dossierId);
  const [{ selectedRun }] = useRunHook(dossierId);
  const [currPage, setCurrPage] = React.useState<number>(
    citationPage ? citationPage : 1
  );
  const [currOcrPages, setCurrOcrPages] = React.useState<
    {
      page: number;
      value: string;
    }[]
  >([]);
  const [searchValue, setSearchValue] = React.useState('');

  const fuse = React.useMemo(() => {
    return new Fuse(currOcrPages, {
      isCaseSensitive: false,
      useExtendedSearch: true,
      keys: ['value'],
    });
  }, [currOcrPages]);

  const filteredOcr = React.useMemo(() => {
    if (!currOcrPages) {
      return [];
    }
    let searchedOcrs = currOcrPages;
    if (searchValue && searchValue.trim() !== '') {
      searchedOcrs = fuse
        .search(`'${searchValue.trim()}`)
        .map((result) => result.item)
        .sort((a, b) => a.page - b.page);
      setCurrPage(searchedOcrs[0]?.page || 1);
    }

    return searchedOcrs;
  }, [searchValue, fuse, currOcrPages]);

  const onPage = React.useCallback(
    (pageNum: number) => {
      setCurrPage(pageNum);
      if (setFilePathUrl) {
        const fileId = params.get('fileId') || file.file_id;
        navigate(
          `${generatePath(EscRouteDefs.Dossier, {
            dossierId,
          })}?fileId=${fileId}&page=${pageNum}${location.hash}`
        );
      }
    },
    [dossierId, file.file_id, location.hash, navigate, params, setFilePathUrl]
  );

  React.useEffect(() => {
    if (!file) {
      setCurrPage(1);
      return;
    }

    let pageNum = citationPage ?? 1;

    if (
      !inDrawer &&
      params.get('page') &&
      !Number.isNaN(Number(params.get('page')))
    ) {
      pageNum = Number(params.get('page'));
    } else if (
      inDrawer &&
      params.get('drawerPage') &&
      !Number.isNaN(Number(params.get('drawerPage')))
    ) {
      pageNum = Number(params.get('drawerPage'));
    }

    setCurrPage(pageNum);
  }, [citationPage, file, params, inDrawer]);

  React.useEffect(() => {
    if (!curTocCitation || !curTocCitation.start_page) {
      return;
    }

    setCurrPage(curTocCitation.start_page);
  }, [curTocCitation]);

  const tabItems: TabsProps['items'] = React.useMemo(() => [], []);

  React.useEffect(() => {
    const graphVersion =
      selectedRun?.data_version ?? claimMetadata?.graph_version;

    if (
      !filesOCR ||
      !file?.file_id ||
      !graphVersion ||
      !filesOCR[file.file_id]
    ) {
      return;
    }

    const ocrPages = filesOCR[file.file_id][graphVersion];

    if (!ocrPages) {
      return;
    }

    setCurrOcrPages(
      Object.entries(ocrPages).map(([key, value]) => ({
        page: Number(key),
        value,
      }))
    );
  }, [
    claimMetadata?.graph_version,
    file.file_id,
    filesOCR,
    selectedRun?.data_version,
  ]);

  if (!isFileViewable(file.ingest_status)) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description={
            messages[getStatusDescriptionLocalePath(file.ingest_status)]
          }
        />
      </div>
    );
  }

  if (!file.total_pages || file.total_pages === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description={messages['claim_overview.files.documents_details.error']}
        />
      </div>
    );
  }

  const pdfProps = {
    currPage,
    filteredOcr,
    setSearchValue,
    inDrawer,
    hasOCR: !!filesOCR?.[file.file_id],
    setCurrPage: onPage,
  };

  return (
    <Layout className={styles.contentContainer} hasSider={tabItems.length > 0}>
      <Layout.Content className={styles.imageContainer}>
        <ClaimFileViewerContainer
          fileId={file.file_id}
          fetchFileContent={filesHandler.fetchFileContents}
          totalPages={file.total_pages}
          {...pdfProps}
        />
      </Layout.Content>

      {tabItems.length > 0 && (
        <Layout.Sider
          className={styles.contentSider}
          theme="light"
          width={350}
          collapsedWidth={0}
          collapsible
          breakpoint="xl"
          reverseArrow
        >
          <Tabs
            items={tabItems}
            style={{
              height: '100%',
              overflowY: 'auto',
            }}
          />
        </Layout.Sider>
      )}
    </Layout>
  );
};

export default ClaimFileDetailsContainer;
